import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bluetooth, RotateCw, Unlock, History } from "lucide-react";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import { fetchDevices, fetchLocations, fetchSockets } from "../services/devices";
import "../styles/device-access-operator.css";
import { apiFetch } from "../utils/apiFetch";
import { isBleUnlockSupported, unlockViaBlePin } from "../services/ble-access";
import { buildDoorEntries } from "../utils/doorEntries";
import { API_BASE_OPERATOR } from "../config/vars";

const DeviceAccessOperator = () => {
    const [devices, setDevices] = useState([]);
    const [locations, setLocations] = useState([]);
    const [sockets, setSockets] = useState([]);
    const [filteredSockets, setFilteredSockets] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [unlockHistory, setUnlockHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [bleSupported] = useState(isBleUnlockSupported());

    const didMountRef = useRef(false);

    const doorEntries = buildDoorEntries({ devices, locations, sockets });

    const getLocalISODate = useCallback((date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }, []);

    const getEffectiveRange = useCallback(() => {
        if (!dateFrom && !dateTo) {
            const today = getLocalISODate(new Date());
            return { dateFrom: today, dateTo: today, isDefaultToday: true };
        }

        const effectiveFrom = dateFrom || dateTo;
        const effectiveTo = dateTo || dateFrom;
        return { dateFrom: effectiveFrom, dateTo: effectiveTo, isDefaultToday: false };
    }, [dateFrom, dateTo, getLocalISODate]);

    const loadUnlockHistory = useCallback(async () => {
        try {
            const range = getEffectiveRange();
            const params = new URLSearchParams();
            params.set("date_from", range.dateFrom);
            params.set("date_to", range.dateTo);
            const res = await apiFetch(
                `${API_BASE_OPERATOR}/sockets/operator/history?${params.toString()}`
            );
            if (res.ok) {
                const history = await res.json();
                setUnlockHistory(history || []);
                setFilteredHistory(history || []);
            }
        } catch (error) {
            console.error("Не вдалося завантажити історію:", error);
        }
    }, [getEffectiveRange]);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [devs, locs, scks] = await Promise.all([
                fetchDevices(),
                fetchLocations(),
                fetchSockets(),
            ]);
            setDevices(devs);
            setLocations(locs);
            setSockets(scks);
            setMessage("");
            await loadUnlockHistory();
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [loadUnlockHistory]);

    useEffect(() => {
        loadData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [loadData]);

    // Перезавантаження історії при зміні фільтра (серверна фільтрація)
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        loadUnlockHistory();
    }, [loadUnlockHistory]);

    // Отримуємо доступні замки для вибраного під'їзду з переліку пристроїв
    useEffect(() => {
        if (!selectedLocation) {
            setFilteredSockets([]);
            return;
        }

        const socketId = parseInt(selectedLocation, 10);
        const selectedDoor = doorEntries.find((entry) => entry.id === socketId);
        setFilteredSockets(selectedDoor ? [selectedDoor] : []);
    }, [selectedLocation, doorEntries]);

    const clearDateFilter = () => {
        setDateFrom("");
        setDateTo("");
    };

    const handleUnlock = async (socketId, deviceName) => {
        try {
            setLoading(true);
            const res = await apiFetch(
                `${API_BASE_OPERATOR}/sockets/operator/${socketId}/unlock`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({}),
                }
            );

            const result = await res.json();

            if (res.ok && result.success) {
                setMessage(`✅ "${deviceName}" розблоковано успішно!`);
                await loadUnlockHistory();
            } else {
                setMessage(
                    `❌ Помилка: ${result.message || result.detail || "Не вдалося розблокувати"}`
                );
            }
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleBleUnlock = async (socket) => {
        const pin = window.prompt(
            `Введіть BLE PIN для ${socket.device_name || "дверей"}`
        );

        if (pin === null) {
            return;
        }

        try {
            setLoading(true);
            await unlockViaBlePin(pin);
            setMessage(`✅ "${socket.device_name}" відкрито через BLE!`);
        } catch (error) {
            setMessage(`❌ BLE помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const renderHistoryForLocation = () => {
        if (!selectedLocation) return [];

        const socketId = parseInt(selectedLocation, 10);
        if (!Number.isFinite(socketId)) return [];

        return filteredHistory.filter((record) => Number(record.socket_id) === socketId);
    };

    const dateFilterActive = Boolean(dateFrom || dateTo);

    const renderHistoryToolbar = () => (
        <div className="config-header device-access-history-dock">
            <div className="header-buttons">
                <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} disabled={loading} title="Оновити" aria-label="Оновити">
                    <RotateCw size={16} />
                </button>
                <FilterPopover
                    active={dateFilterActive}
                    title="Період історії"
                    hint="Без дат показується сьогодні."
                    buttonTitle="Фільтр за датою"
                    onClear={clearDateFilter}
                >
                    <DateRangeFields
                        from={dateFrom}
                        to={dateTo}
                        onFromChange={setDateFrom}
                        onToChange={setDateTo}
                    />
                </FilterPopover>
            </div>
        </div>
    );

    const renderMobileView = () => (
        <div className="device-access-operator-container">
            {!showHistory ? (
                <>
                    <div className="access-header">
                        <h3 className="mobile-page-title">Відкрити замок</h3>
                    </div>

                    {message && <div className="message-box">{message}</div>}
                    {!bleSupported && (
                        <div className="message-box">BLE у цій збірці недоступний. Для офлайн-відкривання потрібен браузер із Web Bluetooth або native BLE plugin.</div>
                    )}

                    <div className="location-select-wrapper">
                        <label>Виберіть підїзд:</label>
                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">— виберіть підїзд —</option>
                            {doorEntries.map((door) => (
                                <option key={door.id} value={door.id}>
                                    {door.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedLocation && (
                        <div className="sockets-grid-mobile">
                            {filteredSockets.length > 0 ? (
                                filteredSockets.map((socket) => (
                                    <div key={socket.id} className="socket-card-mobile">
                                        <h4>{socket.label || socket.device_name}</h4>
                                        {socket.descriptions ? <p className="device-info">{socket.descriptions}</p> : null}
                                        <div className="socket-card-mobile-actions">
                                            <button
                                                className="unlock-button-mobile"
                                                onClick={() => handleUnlock(socket.id, socket.device_name)}
                                                disabled={loading}
                                            >
                                                <Unlock size={18} /> Відкрити
                                            </button>
                                            <button
                                                className="unlock-button-mobile unlock-button-mobile--secondary"
                                                onClick={() => handleBleUnlock(socket)}
                                                disabled={loading || !bleSupported}
                                            >
                                                <Bluetooth size={18} /> BLE + PIN
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="no-sockets">
                                    Для цього підїзду немає розеток
                                </p>
                            )}
                        </div>
                    )}

                    <button
                        className="history-toggle"
                        onClick={() => setShowHistory(true)}
                    >
                        📋 Історія ({unlockHistory.length})
                    </button>
                </>
            ) : (
                <>
                    <div className="access-header">
                        <h3 className="mobile-page-title mobile-page-title--history">Історія розблокувань</h3>
                    </div>

                    <button
                        className="history-toggle"
                        onClick={() => setShowHistory(false)}
                    >
                        ← Назад
                    </button>

                    <div className="history-list">
                        {selectedLocation && renderHistoryForLocation().length > 0 ? (
                            renderHistoryForLocation()
                                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                .map((record, idx) => (
                                    <div key={idx} className={`history-item ${record.success ? "success" : "error"}`}>
                                        <div className="history-status">
                                            {record.success ? "✅" : "❌"}
                                        </div>
                                        <div className="history-info">
                                            <div className="history-time">
                                                {new Date(record.created_at).toLocaleString("uk-UA")}
                                            </div>
                                            <div className="history-source">
                                                Джерело: {record.unlock_source}
                                            </div>
                                            {!record.success && record.error_message && (
                                                <div className="history-error">
                                                    Помилка: {record.error_message}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <p className="no-records">
                                {selectedLocation
                                    ? `Немає записів${(dateFrom || dateTo) ? " за вибраним періодом" : " за сьогодні"}`
                                    : "Виберіть підїзд"}
                            </p>
                        )}
                    </div>

                </>
            )}
            {showHistory ? renderHistoryToolbar() : null}
        </div>
    );

    const renderDesktopView = () => (
        <div className="device-access-operator-container">
            <div className="config-header device-access-header">
                <div>
                    <h2>🔑 Відкрити замок</h2>
                    <p className="operator-page-subtitle">Вибери замок і відкрий його через мережу або BLE. Праворуч доступна історія розблокувань по вибраному замку.</p>
                </div>
                <div className="header-buttons page-header-actions">
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} disabled={loading} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <FilterPopover
                        active={dateFilterActive}
                        title="Період історії"
                        hint="Без дат показується сьогодні."
                        buttonTitle="Фільтр за датою"
                        onClear={clearDateFilter}
                    >
                        <DateRangeFields
                            from={dateFrom}
                            to={dateTo}
                            onFromChange={setDateFrom}
                            onToChange={setDateTo}
                        />
                    </FilterPopover>
                </div>
            </div>

            {message && <div className="message-box">{message}</div>}
            {!bleSupported && (
                <div className="message-box">BLE у цій збірці недоступний. Для офлайн-відкривання потрібен браузер із Web Bluetooth або native BLE plugin.</div>
            )}

            <div className="desktop-layout">
                <div className="desktop-left">
                    <div className="location-select-wrapper">
                        <label>Виберіть підїзд:</label>
                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">— виберіть підїзд —</option>
                            {doorEntries.map((door) => (
                                <option key={door.id} value={door.id}>
                                    {door.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedLocation && (
                        <div className="sockets-grid-desktop">
                            {filteredSockets.length > 0 ? (
                                filteredSockets.map((socket) => (
                                    <div key={socket.id} className="socket-card-desktop">
                                        <h3>{socket.label || socket.device_name}</h3>
                                        <p className="socket-desc">{socket.descriptions}</p>
                                        <button
                                            className="unlock-button"
                                            onClick={() => handleUnlock(socket.id, socket.device_name)}
                                            disabled={loading}
                                        >
                                            <Unlock size={20} /> Відкрити
                                        </button>
                                        <button
                                            className="unlock-button"
                                            onClick={() => handleBleUnlock(socket)}
                                            disabled={loading || !bleSupported}
                                        >
                                            <Bluetooth size={20} /> BLE + PIN
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="no-sockets">Для цього підїзду немає розеток</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="desktop-right">
                    <div className="history-header">
                        <h3>
                            <History size={18} /> Історія розблокувань
                        </h3>
                    </div>

                    {selectedLocation && renderHistoryForLocation().length > 0 ? (
                        <div className="history-table-wrapper">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>Статус</th>
                                        <th>Час</th>
                                        <th>Джерело</th>
                                        <th>IP</th>
                                        <th>Примітки</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderHistoryForLocation()
                                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                        .map((record, idx) => (
                                            <tr key={idx} className={record.success ? "success" : "error"}>
                                                <td>{record.success ? "✅ Успіх" : "❌ Помилка"}</td>
                                                <td>{new Date(record.created_at).toLocaleString("uk-UA")}</td>
                                                <td>{record.unlock_source || "-"}</td>
                                                <td>{record.ip_address || "-"}</td>
                                                <td>{record.error_message || "-"}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="no-records">
                            {selectedLocation
                                ? `Немає записів${(dateFrom || dateTo) ? " за вибраним періодом" : " за сьогодні"}`
                                : "Виберіть підїзд, щоб переглянути історію"}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    return isMobile ? renderMobileView() : renderDesktopView();
};

export default DeviceAccessOperator;
